import type { ReactNode, RefObject } from "react";
import { X } from "lucide-react";
import { cn } from "../lib/utils.js";

/** Header spacing: `comfortable` is today's row, `compact` the 54px dense row. */
export type ExpandedCardModalDensity = "comfortable" | "compact";
/** Header surface: `elevated` adds a card-elevated wash, solid under high contrast. */
export type ExpandedCardModalHeaderVariant = "default" | "elevated";
/** Title type size: `md` is today's 14px, `lg` the 16px/24px `text-heading-3`. */
export type ExpandedCardModalTitleSize = "md" | "lg";

/* Each default entry is today's exact classes, so an option left unset
   renders the header it always did. */
const HEADER_DENSITY: Record<ExpandedCardModalDensity, string> = {
  comfortable: "px-5 py-3",
  compact: "min-h-13.5 px-4 py-2.5",
};
const SUBTITLE_SIZE: Record<ExpandedCardModalDensity, string> = {
  comfortable: "text-xs",
  compact: "text-body-small",
};
const HEADER_VARIANT: Record<ExpandedCardModalHeaderVariant, string> = {
  default: "",
  elevated: "bg-pb-card-elevated/30 hc:bg-pb-card-elevated",
};
const TITLE_SIZE: Record<ExpandedCardModalTitleSize, string> = {
  md: "text-sm font-semibold",
  lg: "text-heading-3",
};

interface ExpandedCardModalHeaderProps {
  title: string;
  /** Referenced by the panel's `aria-labelledby`, so the name IS the visible title. */
  titleId: string;
  onClose: () => void;
  headerRight?: ReactNode;
  headerActionsSlot?: ReactNode;
  titleIcon?: ReactNode;
  titleAccessory?: ReactNode;
  leading?: ReactNode;
  titleClassName?: string | undefined;
  subtitle?: ReactNode;
  headerClassName?: string | undefined;
  density: ExpandedCardModalDensity;
  headerVariant: ExpandedCardModalHeaderVariant;
  titleSize: ExpandedCardModalTitleSize;
  titleRef: RefObject<HTMLSpanElement>;
  focusableTitle: boolean;
}

/** The header row of `ExpandedCardModal`: title block, centred slot, trailing actions and Close. */
export function ExpandedCardModalHeader({
  title,
  titleId,
  onClose,
  headerRight,
  headerActionsSlot,
  titleIcon,
  titleAccessory,
  leading,
  titleClassName,
  subtitle,
  headerClassName,
  density,
  headerVariant,
  titleSize,
  titleRef,
  focusableTitle,
}: ExpandedCardModalHeaderProps): JSX.Element {
  const titleNode = (
    <span
      ref={titleRef}
      id={titleId}
      tabIndex={focusableTitle ? -1 : undefined}
      className={cn(
        "min-w-0 truncate",
        TITLE_SIZE[titleSize],
        "text-pb-text",
        subtitle ? "block" : "sm:col-start-1 sm:row-start-1",
        titleClassName,
      )}
    >
      {subtitle ? null : titleIcon}
      {title}
    </span>
  );
  // The accessory sits BESIDE the title span, never inside it, so it stays out
  // of the dialog's accessible name; it follows the title in the DOM, so Tab
  // reaches it right after the title. Without one the title renders as before.
  const titleRow = titleAccessory ? (
    <div
      className={cn(
        "flex min-w-0 items-center gap-1.5",
        !subtitle && "sm:col-start-1 sm:row-start-1",
      )}
    >
      {titleNode}
      <div className="flex shrink-0 items-center">{titleAccessory}</div>
    </div>
  ) : (
    titleNode
  );
  const titleBlock = subtitle ? (
    // The design's title block: a leading chip beside a title + subtitle
    // stack. The title span keeps the id the dialog is named by, so the
    // subtitle never joins the accessible name.
    <div className="flex min-w-0 items-center gap-3 sm:col-start-1 sm:row-start-1">
      {titleIcon}
      <div className="min-w-0">
        {titleRow}
        <p
          className={cn(
            "mt-0.5 truncate",
            SUBTITLE_SIZE[density],
            "text-pb-text-secondary",
          )}
        >
          {subtitle}
        </p>
      </div>
    </div>
  ) : (
    titleRow
  );
  return (
    <div
      className={cn(
        "grid shrink-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b border-pb-border",
        HEADER_DENSITY[density],
        "sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]",
        HEADER_VARIANT[headerVariant],
        headerClassName,
      )}
    >
      {leading ? (
        // `leading` is a sibling of the title block, never part of the title
        // span, so a Back button does not join the dialog's accessible name.
        <div className="flex min-w-0 items-center gap-2 sm:col-start-1 sm:row-start-1">
          {leading}
          {titleBlock}
        </div>
      ) : (
        titleBlock
      )}
      {headerRight ? (
        <div className="col-span-2 flex min-w-0 justify-center sm:col-span-1 sm:col-start-2 sm:row-start-1">
          {headerRight}
        </div>
      ) : null}
      {/* The trailing group owns the header's last column so `headerActionsSlot`
          and Close cannot land in the same grid cell and overlap. Grouping them
          is also what places the slot's actions where the reader already looks
          for panel chrome; `headerRight` keeps the centre column to itself and
          still stacks below the title on a narrow viewport. */}
      <div className="col-start-2 row-start-1 flex shrink-0 items-center gap-1 sm:col-start-3 sm:justify-self-end">
        {headerActionsSlot}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close expanded view"
          className="shrink-0 rounded-md p-1.5 text-pb-text-muted transition-colors hover:pb-overlay-paint hover:text-pb-text"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
